import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Eye,
  Mail,
  Loader2,
  AlertCircle,
  TrendingUp,
  DollarSign,
  ShoppingBag,
  Users
} from 'lucide-react';
import { preOrderApi } from '../services/api';
import {
  ProductPreOrder,
  ProductPreOrderUpdate,
  PreOrderStatus,
  PreOrderPaymentStatus,
  MarkPickedUpRequest
} from '../types';
import { extractApiError } from '../utils/errorUtils';
import { formatCurrency, addCurrency } from '../utils/currency';
import { useVendor } from '../contexts/VendorContext';

// Status badge styling
const statusStyles: Record<PreOrderStatus, { bg: string; text: string; icon: React.ReactNode }> = {
  pending: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    icon: <Clock className="w-4 h-4" />
  },
  confirmed: {
    bg: 'bg-moonbone',
    text: 'text-ink',
    icon: <CheckCircle className="w-4 h-4" />
  },
  in_production: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    icon: <Package className="w-4 h-4" />
  },
  ready_for_pickup: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    icon: <CheckCircle className="w-4 h-4" />
  },
  picked_up: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    icon: <CheckCircle className="w-4 h-4" />
  },
  cancelled: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    icon: <XCircle className="w-4 h-4" />
  }
};

// Payment status badge styling
const paymentStyles: Record<PreOrderPaymentStatus, { bg: string; text: string }> = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  paid: { bg: 'bg-green-100', text: 'text-green-800' },
  refunded: { bg: 'bg-gray-100', text: 'text-gray-800' }
};

export default function VendorPreorders() {
  const { currentVendor } = useVendor();
  const vendorId = currentVendor?.id;

  // State
  const [preorders, setPreorders] = useState<ProductPreOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<PreOrderStatus | 'all'>('all');
  const [paymentFilter, setPaymentFilter] = useState<PreOrderPaymentStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Selected order for details modal
  const [selectedOrder, setSelectedOrder] = useState<ProductPreOrder | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);

  // Load pre-orders
  const loadPreorders = useCallback(async () => {
    if (!vendorId) return;

    try {
      setLoading(true);
      setError(null);

      // API uses positional parameters: getVendorPreOrders(vendorId, page, pageSize, fairId?, statusFilter?)
      const response = await preOrderApi.getVendorPreOrders(
        vendorId,
        1,    // page
        100   // pageSize - Load all for now, can add pagination later
      );

      setPreorders(response.orders); // Backend returns { orders: [], total: number }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load pre-orders:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    loadPreorders();
  }, [loadPreorders]);

  // Filter pre-orders
  const filteredPreorders = useMemo(() => {
    let filtered = preorders;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Payment filter
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(order => order.payment_status === paymentFilter);
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(order =>
        order.customer_name?.toLowerCase().includes(searchLower) ||
        order.product_name?.toLowerCase().includes(searchLower) ||
        order.confirmation_code?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [preorders, statusFilter, paymentFilter, searchTerm]);

  // Group by status for organization
  const ordersByStatus = useMemo(() => {
    const groups: Record<PreOrderStatus, ProductPreOrder[]> = {
      pending: [],
      confirmed: [],
      in_production: [],
      ready_for_pickup: [],
      picked_up: [],
      cancelled: []
    };

    filteredPreorders.forEach(order => {
      groups[order.status].push(order);
    });

    return groups;
  }, [filteredPreorders]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const total = preorders.length;
    const pending = preorders.filter(o => o.status === 'pending').length;
    const inProgress = preorders.filter(o => o.status === 'in_production').length;
    const ready = preorders.filter(o => o.status === 'ready_for_pickup').length;
    const completed = preorders.filter(o => o.status === 'picked_up').length;

    // Use currency utilities for precision-safe arithmetic (Issue #907, docs/CURRENCY.md)
    const totalRevenue = preorders
      .filter(o => o.payment_status === 'paid')
      .reduce((sum, o) => addCurrency(sum, o.total_price || '0').toFixed(2), '0');

    const completionRate = total > 0 ? (completed / total) * 100 : 0;

    return {
      total,
      pending,
      inProgress,
      ready,
      completed,
      totalRevenue, // Now returns string (Decimal.js format)
      completionRate
    };
  }, [preorders]);

  // Update order status
  const handleUpdateStatus = async (orderId: number, newStatus: PreOrderStatus) => {
    if (!vendorId) return;

    try {
      setUpdatingOrderId(orderId);
      setError(null);

      const updateData: ProductPreOrderUpdate = { status: newStatus };
      await preOrderApi.updatePreOrderStatus(vendorId, orderId, updateData);

      setSuccessMessage(`Order status updated to ${newStatus}`);
      await loadPreorders();

      // Close modal if open
      if (selectedOrder?.id === orderId) {
        setDetailsModalOpen(false);
        setSelectedOrder(null);
      }

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to update order status:', apiError);
      setError(apiError.message);
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Mark as picked up
  const handleMarkPickedUp = async (orderId: number, confirmationCode: string) => {
    if (!vendorId) return;

    try {
      setUpdatingOrderId(orderId);
      setError(null);

      const request: MarkPickedUpRequest = { confirmation_code: confirmationCode };
      await preOrderApi.markPickedUp(vendorId, orderId, request);

      setSuccessMessage('Order marked as picked up successfully');
      await loadPreorders();

      setDetailsModalOpen(false);
      setSelectedOrder(null);

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to mark order as picked up:', apiError);
      setError(apiError.message);
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // View order details
  const handleViewDetails = (order: ProductPreOrder) => {
    setSelectedOrder(order);
    setDetailsModalOpen(true);
  };

  if (!vendorId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
            <p className="text-yellow-800">Please select a vendor profile to view pre-orders.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="vendor-preorders-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Pre-Order Management</h1>
        <p className="text-gray-600">Manage custom orders and track fulfillment</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4" role="alert" aria-live="polite">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4" role="alert" aria-live="assertive">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6" data-testid="metric-total-orders">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.total}</p>
            </div>
            <ShoppingBag className="w-8 h-8 text-secondary" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6" data-testid="metric-pending-orders">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{metrics.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6" data-testid="metric-total-revenue">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(metrics.totalRevenue)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6" data-testid="metric-completion-rate">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completion Rate</p>
              <p className="text-2xl font-bold text-purple-600">{metrics.completionRate.toFixed(0)}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by customer, product, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search pre-orders by customer, product, or confirmation code"
              data-testid="search-preorders"
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PreOrderStatus | 'all')}
              aria-label="Filter pre-orders by status"
              data-testid="filter-status"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_production">In Production</option>
              <option value="ready_for_pickup">Ready for Pickup</option>
              <option value="picked_up">Picked Up</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Payment Filter */}
          <div>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value as PreOrderPaymentStatus | 'all')}
              aria-label="Filter pre-orders by payment status"
              data-testid="filter-payment-status"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal"
            >
              <option value="all">All Payment Statuses</option>
              <option value="pending">Payment Pending</option>
              <option value="paid">Paid</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 text-secondary animate-spin" />
          <span className="ml-2 text-gray-600">Loading pre-orders...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredPreorders.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Pre-Orders Found</h3>
          <p className="text-gray-600">
            {searchTerm || statusFilter !== 'all' || paymentFilter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Pre-orders will appear here when customers place custom orders.'}
          </p>
        </div>
      )}

      {/* Orders List - Organized by Status */}
      {!loading && filteredPreorders.length > 0 && (
        <div className="space-y-6">
          {(['pending', 'confirmed', 'in_production', 'ready_for_pickup', 'picked_up'] as PreOrderStatus[]).map(status => {
            const orders = ordersByStatus[status];
            if (orders.length === 0) return null;

            const statusInfo = statusStyles[status];

            return (
              <div key={status} className="bg-white rounded-lg shadow">
                <div className={`px-6 py-4 border-b border-gray-200 flex items-center ${statusInfo.bg}`}>
                  {statusInfo.icon}
                  <h3 className={`ml-2 text-lg font-semibold capitalize ${statusInfo.text}`}>
                    {status.replace('_', ' ')} ({orders.length})
                  </h3>
                </div>

                <div className="divide-y divide-gray-200">
                  {orders.map(order => (
                    <div key={order.id} className="px-6 py-4 hover:bg-gray-50 transition-colors" data-testid={`order-card-${order.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-2">
                            <h4 className="font-medium text-gray-900">{order.product_name}</h4>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${paymentStyles[order.payment_status].bg} ${paymentStyles[order.payment_status].text}`}>
                              {order.payment_status}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-1" />
                              {order.customer_name}
                            </div>
                            <div>
                              <strong>Qty:</strong> {order.quantity}
                            </div>
                            <div>
                              <strong>Code:</strong> {order.confirmation_code}
                            </div>
                            <div>
                              <strong>Total:</strong> {formatCurrency(order.total_price || '0')}
                            </div>
                          </div>

                          {order.special_instructions && (
                            <div className="mt-2 text-sm text-gray-600 italic">
                              "{order.special_instructions}"
                            </div>
                          )}
                        </div>

                        <div className="ml-4 flex flex-col space-y-2">
                          <button
                            onClick={() => handleViewDetails(order)}
                            className="px-4 py-2 text-sm font-medium text-primary-dark bg-moonbone rounded-md hover:bg-moonbone transition-colors flex items-center"
                            data-testid={`view-order-details-${order.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Details
                          </button>

                          {order.status !== 'picked_up' && order.status !== 'cancelled' && (
                            <div className="relative">
                              <select
                                value={order.status}
                                onChange={(e) => handleUpdateStatus(order.id, e.target.value as PreOrderStatus)}
                                disabled={updatingOrderId === order.id}
                                data-testid={`order-status-select-${order.id}`}
                                className="px-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-dusty-teal focus:border-dusty-teal disabled:opacity-50 w-full"
                              >
                                  <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="in_production">In Production</option>
                                <option value="ready_for_pickup">Ready for Pickup</option>
                              </select>
                              {updatingOrderId === order.id && (
                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                  <Loader2 className="w-4 h-4 animate-spin text-secondary" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Order Details Modal */}
      {detailsModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="order-details-title" data-testid="order-details-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 id="order-details-title" className="text-xl font-bold text-gray-900">Order Details</h2>
                <button
                  onClick={() => setDetailsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close order details dialog"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Order Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Order Information</h3>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Order ID</dt>
                    <dd className="text-sm text-gray-900">#{selectedOrder.id}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Confirmation Code</dt>
                    <dd className="text-sm text-gray-900 font-mono">{selectedOrder.confirmation_code}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Product</dt>
                    <dd className="text-sm text-gray-900">{selectedOrder.product_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Quantity</dt>
                    <dd className="text-sm text-gray-900">{selectedOrder.quantity}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Total Price</dt>
                    <dd className="text-sm text-gray-900">{formatCurrency(selectedOrder.total_price || '0')}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Status</dt>
                    <dd>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[selectedOrder.status].bg} ${statusStyles[selectedOrder.status].text}`}>
                        {selectedOrder.status}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Payment Status</dt>
                    <dd>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${paymentStyles[selectedOrder.payment_status].bg} ${paymentStyles[selectedOrder.payment_status].text}`}>
                        {selectedOrder.payment_status}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Customer Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Information</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Name</dt>
                    <dd className="text-sm text-gray-900">{selectedOrder.customer_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Email</dt>
                    <dd className="text-sm text-gray-900">{selectedOrder.customer_email}</dd>
                  </div>
                </dl>
              </div>

              {/* Special Instructions */}
              {selectedOrder.special_instructions && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Special Instructions</h3>
                  <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
                    {selectedOrder.special_instructions}
                  </p>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Timeline</h3>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-600">Created At</dt>
                    <dd className="text-sm text-gray-900">
                      {new Date(selectedOrder.created_at).toLocaleString()}
                    </dd>
                  </div>
                  {selectedOrder.updated_at && (
                    <div>
                      <dt className="text-sm font-medium text-gray-600">Last Updated</dt>
                      <dd className="text-sm text-gray-900">
                        {new Date(selectedOrder.updated_at).toLocaleString()}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Actions */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
                <div className="space-y-3">
                  {selectedOrder.status === 'ready_for_pickup' && (
                    <button
                      onClick={() => handleMarkPickedUp(selectedOrder.id, selectedOrder.confirmation_code)}
                      disabled={updatingOrderId === selectedOrder.id}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      data-testid="mark-picked-up-button"
                    >
                      {updatingOrderId === selectedOrder.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Mark as Picked Up
                        </>
                      )}
                    </button>
                  )}

                  <a
                    href={`mailto:${selectedOrder.customer_email}`}
                    className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center"
                    data-testid="contact-customer-link"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Contact Customer
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
